import React from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import {
  Page,
  Text,
  Link,
  View,
  StyleSheet,
  Document,
  Font,
} from '@react-pdf/renderer';

import Roboto from 'assets/fonts/Roboto.ttf';
import { maskSensitiveText } from 'utils/maskSensitiveText';

const FIND_ANCHOR_TAG_REGEX = /<a[^>]*>([^<]+)<\/a>/g;
const DATE_FORMAT = 'DD/MMMM/YYYY HH:mm:ss';

Font.register({
  family: 'Roboto',
  src: Roboto,
});

interface TranscriptPDFProps {
  thread: any[];
  title: string;
  endMessage?: string;
  locale?: string;
}

export default function TranscriptPDF({
  thread,
  title,
  endMessage,
  locale,
}: TranscriptPDFProps) {
  dayjs.locale(locale || 'en');

  const formatDateWithCapitalizedMonth = (date: dayjs.Dayjs) =>
    date.format(DATE_FORMAT).replace(/\b\w/g, l => l.toUpperCase());

  const styles = StyleSheet.create({
    text: { lineHeight: 1.5, fontFamily: 'Roboto' },
    endMessage: { marginTop: 15 },
    section: { margin: 30 },
    head: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 30,
      fontFamily: 'Roboto',
    },
  });

  const getLinkMessage = (
    message: string,
    prefix: string,
    keyPrefix: string,
  ) => {
    const anchorWrapper = document.createElement('div');
    anchorWrapper.innerHTML = message;
    const anchor = anchorWrapper.querySelector('a');

    // The regex matched but no parsable <a> element exists: fall back to plain
    // text so the transcript download never throws on a null anchor.
    if (!anchor) {
      return [
        <Text key={`${keyPrefix}-0`} style={styles.text}>
          {`${prefix} ${message}`}
        </Text>,
      ];
    }

    const textArray = message.split(FIND_ANCHOR_TAG_REGEX);
    return textArray.map((str, index) => {
      if (anchor.innerText === str) {
        return (
          <Text key={`${keyPrefix}-${index}`} style={styles.text}>
            {prefix}{' '}
            <Link src={anchor.href}>
              <Text>{str}</Text>
            </Link>
          </Text>
        );
      }
      return (
        <Text key={`${keyPrefix}-${index}`} style={styles.text}>
          {str}
        </Text>
      );
    });
  };

  const getThreadMessages = () => {
    const elements: JSX.Element[] = [];
    thread
      .filter(({ message }) => message)
      .forEach(({ message, pdfOwnerName, timestamp, isSensitive }, index) => {
        if (typeof message !== 'string') return;

        const prefix = `[${formatDateWithCapitalizedMonth(
          dayjs(timestamp),
        )}] ${pdfOwnerName}:`;
        const transcriptMessage = isSensitive
          ? maskSensitiveText(message)
          : message;

        if (transcriptMessage.match(FIND_ANCHOR_TAG_REGEX)) {
          elements.push(
            ...getLinkMessage(transcriptMessage, prefix, `msg-${index}`),
          );
        } else {
          elements.push(
            <Text
              key={`msg-${index}`}
              style={styles.text}
            >{`${prefix} ${transcriptMessage}`}</Text>,
          );
        }
      });
    return elements;
  };

  return (
    <Document>
      <Page size="A4">
        <View style={styles.section}>
          <View style={styles.head}>
            <Text>{title}</Text>
            <Text>{formatDateWithCapitalizedMonth(dayjs())}</Text>
          </View>
          {getThreadMessages()}
          {endMessage && (
            <Text style={{ ...styles.text, ...styles.endMessage }}>
              {endMessage}
            </Text>
          )}
        </View>
      </Page>
    </Document>
  );
}
